/**
 * PlayerSettings — Settings page within the Player Dashboard.
 *
 * Designed for extensibility: the Notifications section is one of potentially
 * many settings sections. New sections (e.g. Privacy, Display) can be added
 * by appending to the SECTIONS array and rendering a new <Section> block.
 *
 * Notification settings are persisted to `player_notification_settings` via
 * useNotificationSettings(). The hook handles upsert logic.
 *
 * Schema references (confirmed from live DB):
 *   player_notification_settings: profile_id, new_photo_album, game_score_posted,
 *     claim_approved, upcoming_game, stat_adjustment_approved,
 *     upcoming_game_hours_before, updated_at
 *
 * Mobile Push Notifications:
 *   Enabling a notification type toggle applies to BOTH in-app and mobile push
 *   delivery simultaneously. The push subscription is linked to the user's
 *   profile_id via the push_subscriptions table so the push-notifications Edge
 *   Function can target them by profile rather than by team.
 *
 *   The push subscription section uses usePushNotifications() to manage the
 *   browser-level Web Push subscription and link it to the user's profile_id.
 */

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  Bell,
  Camera,
  Trophy,
  ShieldCheck,
  Calendar,
  ClipboardCheck,
  CheckCircle2,
  AlertTriangle,
  ChevronDown,
  Settings,
  Clock,
  Smartphone,
  BellRing,
  BellOff,
  Loader2,
  Info,
  ShieldAlert,
  Users,
  ClipboardList,
  Zap,
} from "lucide-react";
import {
  useNotificationSettings,
  NOTIFICATION_TYPE_META,
} from "../hooks/useNotificationSettings";
import { usePushNotifications } from "../hooks/usePushNotifications";
import { useAuth } from "../context/AuthContext";

// ── Icon map for notification types ──────────────────────────────────────────
const NOTIF_ICONS = {
  camera: Camera,
  trophy: Trophy,
  "shield-check": ShieldCheck,
  calendar: Calendar,
  "clipboard-check": ClipboardCheck,
};

// ── Toggle switch ─────────────────────────────────────────────────────────────
const Toggle = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
      transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2
      dark:focus:ring-offset-gray-900
      ${checked ? "bg-orange-500" : "bg-gray-200 dark:bg-gray-700"}
      ${disabled ? "opacity-50 cursor-not-allowed" : ""}
    `}
  >
    <span
      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
        transition duration-200 ease-in-out
        ${checked ? "translate-x-5" : "translate-x-0"}
      `}
    />
  </button>
);

// ── Section wrapper ───────────────────────────────────────────────────────────
const SettingsSection = ({ icon: Icon, title, description, children }) => {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div className="bg-white dark:bg-gray-900 rounded-3xl border border-gray-200 dark:border-gray-800 overflow-hidden">
      {/* Section header */}
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className="w-full flex items-center gap-4 px-6 py-5 text-left hover:bg-gray-50 dark:hover:bg-white/[0.03] transition-colors"
      >
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-2xl bg-orange-500/10">
          <Icon size={20} className="text-orange-500" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-base font-bold text-gray-900 dark:text-white">
            {title}
          </p>
          {description && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {description}
            </p>
          )}
        </div>
        <ChevronDown
          size={16}
          className={`text-gray-400 flex-shrink-0 transition-transform duration-200 ${
            collapsed ? "-rotate-90" : ""
          }`}
        />
      </button>

      {/* Section body */}
      <AnimatePresence initial={false}>
        {!collapsed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="border-t border-gray-100 dark:border-gray-800">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ── Notification row ──────────────────────────────────────────────────────────
const NotificationRow = ({ meta, value, onChange, disabled, isCaptain }) => {
  // Hide captain-only notifications for non-captains
  if (
    meta.roles.length === 1 &&
    meta.roles[0] === "team_captain" &&
    !isCaptain
  ) {
    return null;
  }

  const Icon = NOTIF_ICONS[meta.icon] || Bell;

  return (
    <div className="flex items-center gap-4 px-6 py-4 hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors">
      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-gray-100 dark:bg-gray-800">
        <Icon size={16} className="text-gray-500 dark:text-gray-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900 dark:text-white">
          {meta.label}
          {meta.roles.length === 1 && meta.roles[0] === "team_captain" && (
            <span className="ml-2 text-[10px] font-black tracking-widest uppercase px-1.5 py-0.5 rounded-full bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-500/30">
              Captain
            </span>
          )}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-relaxed">
          {meta.description}
        </p>
      </div>
      <Toggle checked={value} onChange={onChange} disabled={disabled} />
    </div>
  );
};

// ── Hours selector for upcoming game reminder ─────────────────────────────────
const HoursSelector = ({ value, onChange, disabled }) => {
  const OPTIONS = [
    { value: 2, label: "2 hours before" },
    { value: 6, label: "6 hours before" },
    { value: 12, label: "12 hours before" },
    { value: 24, label: "24 hours before" },
    { value: 48, label: "48 hours before" },
    { value: 72, label: "72 hours before" },
  ];

  return (
    <div className="flex items-center gap-4 px-6 py-4 bg-orange-50/50 dark:bg-orange-500/5 border-t border-orange-100 dark:border-orange-500/10">
      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-orange-100 dark:bg-orange-500/15">
        <Clock size={16} className="text-orange-500" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900 dark:text-white">
          Reminder Timing
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
          How far in advance to send the game reminder
        </p>
      </div>
      <select
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        disabled={disabled}
        className="text-sm font-medium rounded-xl border border-gray-200 dark:border-gray-700
          bg-white dark:bg-gray-800 text-gray-900 dark:text-white
          px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500
          disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
};

// ── Mobile Push Notifications section ────────────────────────────────────────
/**
 * PushNotificationsSection
 *
 * Manages the browser-level Web Push subscription for this user.
 * When subscribed, the user's profile_id is linked to the push_subscriptions
 * row so the push-notifications Edge Function can target them by profile.
 *
 * The toggle here controls whether the device receives push notifications at all.
 * Individual notification type toggles (in the Notifications section above) control
 * which types are delivered — both in-app AND as push simultaneously.
 *
 * This component is intentionally outside the <form> so its actions are
 * independent of the notification settings save flow.
 */
const PushNotificationsSection = ({ profileId }) => {
  const {
    isSupported,
    isIOS,
    isStandalone,
    isSubscribed,
    isLoading,
    error: pushError,
    subscribe,
    unsubscribe,
  } = usePushNotifications();

  const [localError, setLocalError] = useState(null);
  const [justSubscribed, setJustSubscribed] = useState(false);

  // Clear success flash after 3s
  useEffect(() => {
    if (!justSubscribed) return;
    const t = setTimeout(() => setJustSubscribed(false), 3000);
    return () => clearTimeout(t);
  }, [justSubscribed]);

  const handleToggle = async () => {
    setLocalError(null);
    if (isSubscribed) {
      // Unsubscribe — pass the "__profile__" sentinel so the hook removes the
      // subscription entirely. Also pass profileId as fallback for the
      // deleteByProfile Edge Function action in case the SW subscription was lost.
      await unsubscribe("__profile__", profileId);
    } else {
      // Subscribe — link this device to the user's profile_id.
      // We pass "__profile__" as the teamId sentinel so the subscription row
      // is created with team_ids=[] but profile_id set, enabling sendToProfile.
      await subscribe("__profile__", profileId);
      if (!pushError) {
        setJustSubscribed(true);
      }
    }
  };

  // ── iOS not installed ──────────────────────────────────────────────────────
  if (isIOS && !isStandalone) {
    return (
      <div className="px-6 py-5">
        <div className="flex items-start gap-3 p-4 rounded-2xl bg-blue-50 dark:bg-blue-500/10 border border-blue-100 dark:border-blue-500/20">
          <Info size={16} className="text-blue-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-blue-700 dark:text-blue-300">
              Add to Home Screen first
            </p>
            <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5 leading-relaxed">
              On iPhone and iPad, push notifications require the app to be
              installed. Tap the Share button in Safari, then "Add to Home
              Screen", and reopen the app from there.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Browser doesn't support push ───────────────────────────────────────────
  if (!isSupported) {
    return (
      <div className="px-6 py-5">
        <div className="flex items-start gap-3 p-4 rounded-2xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          <BellOff size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Your browser doesn't support push notifications. Try Chrome, Edge,
            or Firefox on desktop, or install the app on your phone.
          </p>
        </div>
      </div>
    );
  }

  // ── Error display helper ───────────────────────────────────────────────────
  const displayError = localError || pushError;
  const errorMessage =
    displayError === "permission-denied"
      ? "Notification permission was denied. Please enable it in your browser settings and try again."
      : displayError === "ios-not-installed"
        ? "Add the app to your Home Screen first to enable push notifications on iOS."
        : displayError
          ? "Something went wrong. Please try again."
          : null;

  return (
    <div className="divide-y divide-gray-100 dark:divide-gray-800">
      {/* Main toggle row */}
      <div className="flex items-center gap-4 px-6 py-4 hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors">
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-gray-100 dark:bg-gray-800">
          {isLoading ? (
            <Loader2 size={16} className="text-orange-500 animate-spin" />
          ) : isSubscribed ? (
            <BellRing size={16} className="text-orange-500" />
          ) : (
            <BellOff size={16} className="text-gray-400 dark:text-gray-500" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 dark:text-white">
            Mobile Push Notifications
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-relaxed">
            {isSubscribed
              ? "This device will receive push notifications for your enabled alert types."
              : "Enable to receive alerts on this device even when the app isn't open."}
          </p>
        </div>
        <Toggle
          checked={isSubscribed}
          onChange={handleToggle}
          disabled={isLoading}
        />
      </div>

      {/* Success flash */}
      <AnimatePresence>
        {justSubscribed && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="mx-6 my-3 flex items-center gap-2 text-emerald-600 dark:text-emerald-400 text-sm font-semibold">
              <CheckCircle2 size={15} />
              Push notifications enabled for this device!
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error display */}
      <AnimatePresence>
        {errorMessage && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="mx-6 my-3 flex items-start gap-2 text-red-600 dark:text-red-400 text-sm">
              <AlertTriangle size={15} className="mt-0.5 flex-shrink-0" />
              {errorMessage}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Informational note when subscribed */}
      {isSubscribed && (
        <div className="px-6 py-4 bg-orange-50/40 dark:bg-orange-500/5">
          <div className="flex items-start gap-2.5">
            <Info
              size={14}
              className="text-orange-400 dark:text-orange-500 mt-0.5 flex-shrink-0"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
              Push notifications mirror your in-app alert settings above.
              Disabling a notification type will stop both in-app and push
              delivery for that type.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Admin Alerts section (informational, no toggles) ─────────────────────────
/**
 * Shown to admin users in place of (or in addition to) the player notification
 * toggles. Admin alerts are always-on — they fire via DB triggers on INSERT to
 * player_claims, registration_submissions, and stat_adjustment_requests.
 * There is no per-type opt-out because these are operational alerts that every
 * admin needs to act on.
 */
const ADMIN_ALERT_TYPES = [
  {
    icon: ShieldAlert,
    label: "New Player Claim Requests",
    description:
      "Fires when a player submits a claim request for a player profile. Directs you to the Claims tab.",
    color: "text-red-500 dark:text-red-400",
    bg: "bg-red-50 dark:bg-red-500/10",
  },
  {
    icon: Users,
    label: "New Team Registrations",
    description:
      "Fires when a captain submits a new team registration. Directs you to the Registrations tab.",
    color: "text-red-500 dark:text-red-400",
    bg: "bg-red-50 dark:bg-red-500/10",
  },
  {
    icon: ClipboardList,
    label: "New Stat Adjustment Requests",
    description:
      "Fires when a captain submits a stat correction request. Directs you to the Adjustments tab.",
    color: "text-red-500 dark:text-red-400",
    bg: "bg-red-50 dark:bg-red-500/10",
  },
];

const AdminAlertsSection = () => (
  <SettingsSection
    icon={ShieldAlert}
    title="Admin Alerts"
    description="Automatic notifications for actions that require your review."
  >
    <div className="divide-y divide-gray-100 dark:divide-gray-800">
      {ADMIN_ALERT_TYPES.map((alert) => {
        const Icon = alert.icon;
        return (
          <div key={alert.label} className="flex items-center gap-4 px-6 py-4">
            <div
              className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl ${alert.bg}`}
            >
              <Icon size={16} className={alert.color} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  {alert.label}
                </p>
                <span className="text-[9px] font-black tracking-widest uppercase px-1.5 py-0.5 rounded-full border bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400 border-red-200 dark:border-red-500/30">
                  Always On
                </span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-relaxed">
                {alert.description}
              </p>
            </div>
            {/* Static "on" indicator — not a toggle */}
            <Zap size={16} className="text-orange-400 flex-shrink-0" />
          </div>
        );
      })}
    </div>
    {/* Informational note */}
    <div className="px-6 py-4 bg-red-50/40 dark:bg-red-500/5 border-t border-red-100 dark:border-red-500/10">
      <div className="flex items-start gap-2.5">
        <Info
          size={14}
          className="text-red-400 dark:text-red-500 mt-0.5 flex-shrink-0"
        />
        <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
          Admin alerts are always active and cannot be disabled. They appear in
          your notification drawer and are also delivered as push notifications
          if you have this device subscribed below.
        </p>
      </div>
    </div>
  </SettingsSection>
);

// ── Main component ────────────────────────────────────────────────────────────
const PlayerSettings = () => {
  const { user, isCaptain, isAdmin, isPlayer } = useAuth();
  const profileId = user?.id ?? null;
  // A "pure admin" is someone with the admin role but no player/captain profile.
  // They see admin alerts info but not the player notification toggles.
  const isPureAdmin = isAdmin && !isPlayer && !isCaptain;

  const {
    settings,
    loading,
    saving,
    saved,
    error,
    updateSetting,
    saveSettings,
  } = useNotificationSettings(profileId);

  const handleSave = async (e) => {
    e.preventDefault();
    await saveSettings();
  };

  if (!user) {
    return (
      <div className="py-16 text-center text-gray-500 dark:text-gray-400">
        <p className="text-sm">Sign in to manage your settings.</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Page header */}
      <div className="flex items-center gap-3 mb-2">
        <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-orange-500/10">
          <Settings size={20} className="text-orange-500" />
        </div>
        <h2 className="text-2xl font-semibold tracking-tight dark:text-white">
          Settings
        </h2>
      </div>

      {/* ── Admin Alerts section (always-on, informational) ───────────────────
           Shown to all admins. Pure admins see ONLY this section (no player
           toggles). Admins who are also players/captains see both sections.
      ──────────────────────────────────────────────────────────────────────── */}
      {isAdmin && <AdminAlertsSection />}

      {/* ── Player / captain notification toggles ────────────────────────────
           Hidden for pure admins — they have no team, no games, and no player
           claims of their own, so the player toggles are irrelevant.
      ──────────────────────────────────────────────────────────────────────── */}
      {!isPureAdmin && (
        <form onSubmit={handleSave} className="space-y-4">
          {/* ── Notifications section ───────────────────────────────────────── */}
          <SettingsSection
            icon={Bell}
            title="Notifications"
            description="Choose which in-app and push alerts you want to receive."
          >
            {loading ? (
              <div className="px-6 py-8 text-center text-sm text-gray-400 dark:text-gray-500">
                Loading settings…
              </div>
            ) : (
              <>
                <div className="divide-y divide-gray-100 dark:divide-gray-800">
                  {NOTIFICATION_TYPE_META.map((meta) => (
                    <NotificationRow
                      key={meta.key}
                      meta={meta}
                      value={settings[meta.key] ?? true}
                      onChange={(val) => updateSetting(meta.key, val)}
                      disabled={saving}
                      isCaptain={isCaptain}
                    />
                  ))}
                </div>

                {/* Upcoming game timing — only shown when upcoming_game is enabled */}
                <AnimatePresence>
                  {settings.upcoming_game && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.15 }}
                      className="overflow-hidden"
                    >
                      <HoursSelector
                        value={settings.upcoming_game_hours_before ?? 24}
                        onChange={(val) =>
                          updateSetting("upcoming_game_hours_before", val)
                        }
                        disabled={saving}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </>
            )}
          </SettingsSection>

          {/* ── Save button + feedback ──────────────────────────────────────── */}
          <div className="flex items-center gap-4">
            <button
              type="submit"
              disabled={saving || loading}
              className="px-6 py-3 bg-orange-600 hover:bg-orange-700 disabled:bg-orange-400
                text-white text-sm font-bold rounded-2xl transition-all active:scale-[0.98]
                disabled:cursor-not-allowed"
            >
              {saving ? "Saving…" : "Save Settings"}
            </button>

            <AnimatePresence>
              {saved && (
                <motion.div
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0 }}
                  className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400 text-sm font-semibold"
                >
                  <CheckCircle2 size={16} />
                  Saved!
                </motion.div>
              )}
              {error && (
                <motion.div
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0 }}
                  className="flex items-center gap-1.5 text-red-600 dark:text-red-400 text-sm font-semibold"
                >
                  <AlertTriangle size={16} />
                  {error}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </form>
      )}

      {/* ── Mobile Push Notifications section ────────────────────────────────
           Intentionally outside the <form> — push subscription state is managed
           independently of the notification settings save flow. The toggle here
           controls whether this device receives push at all; the type toggles
           above control which types are delivered (both in-app and push). -->
      ──────────────────────────────────────────────────────────────────────── */}
      <SettingsSection
        icon={Smartphone}
        title="Mobile Push Notifications"
        description="Receive alerts on this device even when the app isn't open."
      >
        <PushNotificationsSection profileId={profileId} />
      </SettingsSection>

      {/* ── Future sections placeholder ───────────────────────────────────── */}
      {/* Additional settings sections (e.g. Privacy, Display) can be added
          here as new <SettingsSection> blocks without touching the notification
          logic above. */}
    </div>
  );
};

export default PlayerSettings;
